import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import {
  Sprout, Eye, Lightbulb, HelpCircle, Search, Star, Brain, Feather,
  ArrowRight, RefreshCw, Quote,
} from "lucide-react";

const L = {
  fr: {
    eyebrow: "De la bulle à l'envol",
    title: "La curiosité est l'engrais du savoir",
    subtitle: "Nourris ta curiosité, elle fera pousser ton savoir.",
    centerLabel: "La curiosité",
    centerSub: "est l'engrais du savoir",
    subLabels: ["Savoir", "Connaissance", "Expérience", "Compréhension"],
    loopNote: "Une réponse fait naître de nouvelles questions.",
    cycleTitle: "Le cycle vertueux",
    quote: "Pose des questions. Explore. Découvre. Comprends. Et recommence. C'est ainsi que tu grandis.",
    steps: [
      { name: "L'Inconnu", text: "Quelque chose attire notre attention.", prompt: "Qu'est-ce qui, en ce moment, attire mon attention sans que je sache vraiment pourquoi ?" },
      { name: "L'Étonnement", text: "« Tiens… pourquoi ? »", prompt: "Quelle chose que je crois banale devient étrange si j'y regarde de plus près ?" },
      { name: "La Question", text: "Le cerveau cherche à combler un manque.", prompt: "Quelle question me trotte dans la tête et cherche une réponse ?" },
      { name: "L'Exploration", text: "On cherche, observe, écoute, compare, expérimente.", prompt: "Par où commencer pour explorer un sujet qui m'intrigue vraiment ?" },
      { name: "La Découverte", text: "On rencontre une nouvelle information ou expérience.", prompt: "Quelle idée récente a changé ma façon de voir quelque chose ?" },
      { name: "La Compréhension", text: "Les éléments commencent à prendre du sens.", prompt: "Comment relier ce que je viens d'apprendre à ce que je savais déjà ?" },
      { name: "La Transformation", text: "On ne voit plus le monde exactement comme avant.", prompt: "En quoi une découverte peut-elle transformer ma façon de voir le monde ?" },
    ],
    chips: ["Curiosité", "Question", "Exploration", "Savoir", "Compréhension", "Nouvelle curiosité"],
  },
  en: {
    eyebrow: "From cocoon to flight",
    title: "Curiosity is the fertiliser of knowledge",
    subtitle: "Feed your curiosity, and it will grow your knowledge.",
    centerLabel: "Curiosity",
    centerSub: "is the fertiliser of knowledge",
    subLabels: ["Knowledge", "Learning", "Experience", "Understanding"],
    loopNote: "An answer gives birth to new questions.",
    cycleTitle: "The virtuous cycle",
    quote: "Ask questions. Explore. Discover. Understand. And begin again. That's how you grow.",
    steps: [
      { name: "The Unknown", text: "Something catches our attention.", prompt: "What is catching my attention right now, without me really knowing why?" },
      { name: "Wonder", text: "“Wait… why?”", prompt: "What ordinary thing turns strange when I look at it more closely?" },
      { name: "The Question", text: "The brain seeks to fill a gap.", prompt: "What question keeps running through my mind, waiting for an answer?" },
      { name: "Exploration", text: "We search, observe, listen, compare, experiment.", prompt: "Where should I start to explore a subject that truly intrigues me?" },
      { name: "Discovery", text: "We meet a new piece of information or experience.", prompt: "What recent idea has changed the way I see something?" },
      { name: "Understanding", text: "The pieces start to make sense.", prompt: "How can I connect what I just learned to what I already knew?" },
      { name: "Transformation", text: "We no longer see the world quite the same way.", prompt: "How could a discovery transform the way I see the world?" },
    ],
    chips: ["Curiosity", "Question", "Exploration", "Knowledge", "Understanding", "New curiosity"],
  },
  es: {
    eyebrow: "Del capullo al vuelo",
    title: "La curiosidad es el abono del saber",
    subtitle: "Alimenta tu curiosidad y hará crecer tu saber.",
    centerLabel: "La curiosidad",
    centerSub: "es el abono del saber",
    subLabels: ["Saber", "Conocimiento", "Experiencia", "Comprensión"],
    loopNote: "Una respuesta hace nacer nuevas preguntas.",
    cycleTitle: "El ciclo virtuoso",
    quote: "Haz preguntas. Explora. Descubre. Comprende. Y vuelve a empezar. Así es como creces.",
    steps: [
      { name: "Lo Desconocido", text: "Algo llama nuestra atención.", prompt: "¿Qué está llamando mi atención ahora mismo, sin saber muy bien por qué?" },
      { name: "El Asombro", text: "«Anda… ¿por qué?»", prompt: "¿Qué cosa que creo banal se vuelve extraña si la miro de cerca?" },
      { name: "La Pregunta", text: "El cerebro busca llenar un vacío.", prompt: "¿Qué pregunta ronda mi cabeza esperando una respuesta?" },
      { name: "La Exploración", text: "Buscamos, observamos, escuchamos, comparamos, experimentamos.", prompt: "¿Por dónde empezar para explorar un tema que de verdad me intriga?" },
      { name: "El Descubrimiento", text: "Encontramos una nueva información o experiencia.", prompt: "¿Qué idea reciente ha cambiado mi forma de ver algo?" },
      { name: "La Comprensión", text: "Las piezas empiezan a cobrar sentido.", prompt: "¿Cómo conectar lo que acabo de aprender con lo que ya sabía?" },
      { name: "La Transformación", text: "Ya no vemos el mundo exactamente igual.", prompt: "¿Cómo podría un descubrimiento transformar mi forma de ver el mundo?" },
    ],
    chips: ["Curiosidad", "Pregunta", "Exploración", "Saber", "Comprensión", "Nueva curiosidad"],
  },
  nl: {
    eyebrow: "Van cocon tot vlucht",
    title: "Nieuwsgierigheid is de mest van kennis",
    subtitle: "Voed je nieuwsgierigheid en ze laat je kennis groeien.",
    centerLabel: "Nieuwsgierigheid",
    centerSub: "is de mest van kennis",
    subLabels: ["Weten", "Kennis", "Ervaring", "Begrip"],
    loopNote: "Een antwoord doet nieuwe vragen ontstaan.",
    cycleTitle: "De vruchtbare cyclus",
    quote: "Stel vragen. Verken. Ontdek. Begrijp. En begin opnieuw. Zo groei je.",
    steps: [
      { name: "Het Onbekende", text: "Iets trekt onze aandacht.", prompt: "Wat trekt op dit moment mijn aandacht, zonder dat ik echt weet waarom?" },
      { name: "Verwondering", text: "“Hé… waarom?”", prompt: "Welk alledaags iets wordt vreemd als ik er beter naar kijk?" },
      { name: "De Vraag", text: "Het brein wil een leemte opvullen.", prompt: "Welke vraag blijft door mijn hoofd spoken en zoekt een antwoord?" },
      { name: "Verkenning", text: "We zoeken, observeren, luisteren, vergelijken, experimenteren.", prompt: "Waar begin ik om een onderwerp te verkennen dat me echt intrigeert?" },
      { name: "De Ontdekking", text: "We stuiten op nieuwe informatie of ervaring.", prompt: "Welk recent idee heeft mijn kijk op iets veranderd?" },
      { name: "Het Begrip", text: "De stukjes beginnen betekenis te krijgen.", prompt: "Hoe verbind ik wat ik net leerde met wat ik al wist?" },
      { name: "De Transformatie", text: "We zien de wereld niet meer helemaal hetzelfde.", prompt: "Hoe zou een ontdekking mijn kijk op de wereld kunnen veranderen?" },
    ],
    chips: ["Nieuwsgierigheid", "Vraag", "Verkenning", "Kennis", "Begrip", "Nieuwe nieuwsgierigheid"],
  },
};

// Icon + accent hue per step (echoes the infographic palette, tuned for dark background)
const STEP_META = [
  { Icon: Eye, hue: 140 },
  { Icon: Lightbulb, hue: 165 },
  { Icon: HelpCircle, hue: 188 },
  { Icon: Search, hue: 210 },
  { Icon: Star, hue: 268 },
  { Icon: Brain, hue: 330 },
  { Icon: Feather, hue: 30 },
];

const GoldButterfly = () => (
  <motion.div
    className="absolute -top-4 -right-3 pointer-events-none z-20"
    data-testid="chemin-cycle-butterfly"
    initial={{ x: 0, y: 0, opacity: 0 }}
    animate={{ x: [0, 10, -6, 16, 26], y: [0, -16, -34, -54, -78], opacity: [0, 1, 1, 0.65, 0], rotate: [0, -10, 7, -6, 5] }}
    transition={{ duration: 7, repeat: Infinity, repeatDelay: 2.4, ease: "easeInOut" }}
    style={{ width: 26, height: 26 }}
  >
    <motion.div animate={{ scaleX: [1, 0.5, 1] }} transition={{ duration: 0.32, repeat: Infinity, ease: "easeInOut" }} style={{ width: "100%", height: "100%", filter: "drop-shadow(0 0 6px hsl(38 95% 60% / 0.6))" }}>
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <defs>
          <linearGradient id="bfly-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="hsl(46 96% 72%)" />
            <stop offset="100%" stopColor="hsl(30 92% 52%)" />
          </linearGradient>
        </defs>
        <ellipse cx="24" cy="24" rx="13" ry="16" fill="url(#bfly-gold)" transform="rotate(-22 24 24)" />
        <ellipse cx="40" cy="24" rx="13" ry="16" fill="url(#bfly-gold)" transform="rotate(22 40 24)" />
        <ellipse cx="26" cy="42" rx="9" ry="12" fill="url(#bfly-gold)" transform="rotate(-14 26 42)" />
        <ellipse cx="38" cy="42" rx="9" ry="12" fill="url(#bfly-gold)" transform="rotate(14 38 42)" />
        <rect x="30.8" y="19" width="2.4" height="29" rx="1.2" fill="hsl(28 45% 20%)" />
      </svg>
    </motion.div>
  </motion.div>
);

const Emblem = ({ c }) => (
  <div className="w-full rounded-3xl border border-primary/25 bg-white/[0.04] backdrop-blur-xl px-6 py-8 text-center shadow-2xl">
    <span className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center bg-primary/12 border border-primary/30"
      style={{ boxShadow: "0 0 30px hsl(32 90% 55% / 0.25)" }}>
      <Sprout className="text-primary" size={26} strokeWidth={1.5} />
    </span>
    <h3 className="font-display text-2xl text-gradient-orange leading-tight">{c.centerLabel}</h3>
    <p className="font-display italic text-foreground/55 text-sm mb-4">{c.centerSub}</p>
    <div className="flex flex-wrap justify-center gap-1.5">
      {c.subLabels.map((s, i) => (
        <span key={i} className="bfly-word text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border border-primary/20 bg-primary/[0.06] text-primary/75">{s}</span>
      ))}
    </div>
  </div>
);

const StepNode = ({ i, step, compact, active }) => {
  const { Icon, hue } = STEP_META[i];
  const accent = `hsl(${hue} 72% 60%)`;
  return (
    <div className="relative rounded-2xl border bg-white/[0.03] backdrop-blur-md p-4 sm:p-5 flex flex-col h-full transition-shadow"
      style={{
        borderColor: active ? `hsl(${hue} 72% 60% / 0.75)` : `hsl(${hue} 60% 55% / 0.35)`,
        boxShadow: active ? `0 18px 50px -12px hsl(${hue} 70% 45% / 0.55), 0 0 24px hsl(${hue} 75% 55% / 0.35)` : "none",
        background: active ? `hsl(${hue} 60% 45% / 0.12)` : undefined,
      }}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="w-8 h-8 rounded-full flex items-center justify-center font-display text-sm font-semibold flex-shrink-0"
          style={{ background: `hsl(${hue} 65% 50% / 0.18)`, color: accent, border: `1px solid hsl(${hue} 65% 55% / 0.4)` }}>
          {i + 1}
        </span>
        <Icon size={18} strokeWidth={1.7} style={{ color: accent }} className="flex-shrink-0" />
      </div>
      <h4 className={`font-display text-foreground/90 leading-tight mb-1 ${compact ? "text-base" : "text-lg"}`}>{step.name}</h4>
      <p className={`text-foreground/55 leading-relaxed ${compact ? "text-[11px]" : "text-sm"}`}>{step.text}</p>
      {i === 6 && <GoldButterfly />}
    </div>
  );
};

export const CycleVertueux = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = L[lang] || L.fr;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  const openInFil = (prompt) => {
    if (!prompt) return;
    try { localStorage.setItem("coccon_pending_prompt", prompt); } catch { /* ignore */ }
    navigate(user ? "/atelier" : "/entrer");
  };

  // Hover manager: keeps the halo alive when the cursor moves from a step onto the halo.
  const clearTimer = useRef(null);
  const setHover = (i) => { if (clearTimer.current) { clearTimeout(clearTimer.current); clearTimer.current = null; } setHovered(i); };
  const keepHover = () => { if (clearTimer.current) { clearTimeout(clearTimer.current); clearTimer.current = null; } };
  const scheduleClear = () => { if (clearTimer.current) clearTimeout(clearTimer.current); clearTimer.current = setTimeout(() => setHovered(null), 160); };

  // Wheel geometry
  const SIZE = 780;
  const R = 292;
  const NODE_W = 176;

  return (
    <section className="relative px-6 py-6 sm:py-10" data-testid="chemin-cycle-section">
      <div className="max-w-5xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-16 -translate-x-1/2 w-[620px] h-[620px] rounded-full bg-primary/10 blur-[130px]" />
          </div>

          <div className="relative z-10 px-6 sm:px-12 py-14">
            {/* Header */}
            <div className="max-w-2xl mx-auto text-center">
              <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-4">
                <Feather size={14} /> {c.eyebrow}
              </p>
              <h2 className="font-display text-3xl sm:text-5xl text-gradient-orange leading-[1.12] pb-[0.08em]" data-testid="chemin-cycle-title">{c.title}</h2>
              <p className="mt-3 font-display italic text-primary/70 text-lg sm:text-xl">{c.subtitle}</p>
            </div>

            {/* ===== Desktop : circular wheel ===== */}
            <div className="hidden lg:flex justify-center mt-10">
              <motion.div
                className="relative"
                style={{ width: SIZE, height: SIZE }}
                initial={{ rotate: -16, scale: 0.88, opacity: 0 }}
                whileInView={{ rotate: 0, scale: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ type: "spring", stiffness: 55, damping: 15 }}
                data-testid="chemin-cycle-wheel"
              >
                {/* slowly rotating dashed ring */}
                <svg className="absolute inset-0 w-full h-full animate-[spin_60s_linear_infinite]" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
                  <circle cx={SIZE / 2} cy={SIZE / 2} r={R} fill="none" stroke="hsl(28 96% 56% / 0.28)" strokeWidth="1.5" strokeDasharray="2 12" strokeLinecap="round" />
                </svg>
                {/* pulsing glow ring (fires as it scrolls into view, keeps breathing) */}
                <motion.div
                  className="absolute left-1/2 top-1/2 rounded-full pointer-events-none"
                  style={{ width: R * 2, height: R * 2, x: "-50%", y: "-50%", border: "1px solid hsl(28 96% 56% / 0.18)" }}
                  animate={{ scale: [1, 1.045, 1], opacity: [0.4, 0.85, 0.4], boxShadow: ["0 0 0 hsl(28 96% 56% / 0)", "0 0 60px hsl(28 96% 56% / 0.22)", "0 0 0 hsl(28 96% 56% / 0)"] }}
                  transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                />
                {/* roaming halo that follows the hovered step along the ring — clickable */}
                <motion.div
                  className="absolute left-1/2 top-1/2 rounded-full"
                  style={{ width: 150, height: 150, marginLeft: -75, marginTop: -75, pointerEvents: hovered !== null ? "auto" : "none", cursor: "pointer" }}
                  data-testid="chemin-cycle-halo"
                  initial={false}
                  onMouseEnter={keepHover}
                  onMouseLeave={scheduleClear}
                  onClick={() => hovered !== null && openInFil(c.steps[hovered].prompt)}
                  animate={hovered !== null
                    ? { x: Math.cos((-90 + hovered * (360 / 7)) * (Math.PI / 180)) * R, y: Math.sin((-90 + hovered * (360 / 7)) * (Math.PI / 180)) * R, opacity: 1, scale: 1 }
                    : { opacity: 0, scale: 0.6 }}
                  transition={{ type: "spring", stiffness: 170, damping: 20 }}
                >
                  <motion.div
                    className="w-full h-full rounded-full pointer-events-none"
                    style={{ background: `radial-gradient(circle, hsl(${STEP_META[hovered ?? 0].hue} 85% 60% / 0.55), hsl(${STEP_META[hovered ?? 0].hue} 85% 55% / 0.12) 45%, transparent 70%)`, filter: "blur(6px)" }}
                    animate={{ scale: [1, 1.15, 1], opacity: [0.75, 1, 0.75] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                </motion.div>
                {/* center emblem */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: 300 }}>
                  <Emblem c={c} />
                </div>
                {/* step nodes on the circle */}
                {c.steps.map((s, i) => {
                  const a = (-90 + i * (360 / 7)) * (Math.PI / 180);
                  const x = Math.cos(a) * R;
                  const y = Math.sin(a) * R;
                  const isActive = hovered === i;
                  const isDimmed = hovered !== null && hovered !== i;
                  return (
                    <div
                      key={i}
                      className="absolute"
                      style={{ left: "50%", top: "50%", width: NODE_W, transform: `translate(-50%, -50%) translate(${x}px, ${y}px)` }}
                    >
                      <motion.div
                        onHoverStart={() => setHover(i)}
                        onHoverEnd={scheduleClear}
                        onClick={() => openInFil(s.prompt)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openInFil(s.prompt); }}
                        animate={{ scale: isActive ? 1.12 : isDimmed ? 0.94 : 1, opacity: isDimmed ? 0.32 : 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 22 }}
                        style={{ zIndex: isActive ? 30 : 10, filter: isDimmed ? "saturate(0.6)" : "none", cursor: "pointer" }}
                        data-testid={`chemin-cycle-step-${i}`}
                      >
                        <StepNode i={i} step={s} compact active={isActive} />
                      </motion.div>
                    </div>
                  );
                })}
              </motion.div>
            </div>
            <p className="hidden lg:block text-center font-display italic text-primary/70 text-sm mt-4 flex items-center justify-center gap-2">
              <RefreshCw size={14} className="inline text-primary/70" /> {c.loopNote}
            </p>

            {/* ===== Mobile / tablet : emblem + grid fallback ===== */}
            <div className="lg:hidden">
              <div className="mt-10 flex justify-center">
                <div className="w-full max-w-sm"><Emblem c={c} /></div>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="chemin-cycle-steps">
                {c.steps.map((s, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    onClick={() => openInFil(s.prompt)}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openInFil(s.prompt); }}
                    style={{ cursor: "pointer" }}
                    data-testid={`chemin-cycle-step-m-${i}`}>
                    <StepNode i={i} step={s} />
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 7 * 0.06 }}
                  data-testid="chemin-cycle-loop"
                  className="rounded-2xl border border-primary/30 bg-primary/[0.06] backdrop-blur-md p-5 flex flex-col items-center justify-center text-center">
                  <RefreshCw className="text-primary animate-[spin_8s_linear_infinite] mb-3" size={26} strokeWidth={1.6} />
                  <p className="font-display italic text-primary/80 text-sm leading-relaxed">{c.loopNote}</p>
                </motion.div>
              </div>
            </div>

            {/* Virtuous cycle strip */}
            <div className="mt-12 rounded-2xl border border-primary/15 bg-white/[0.02] px-5 sm:px-8 py-7">
              <p className="text-center uppercase tracking-[0.22em] text-[11px] text-primary/70 mb-5">{c.cycleTitle}</p>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                {c.chips.map((chip, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3">
                    <span className="bfly-word text-xs sm:text-sm font-medium text-foreground/70 whitespace-nowrap">{chip}</span>
                    {i < c.chips.length - 1
                      ? <ArrowRight size={15} className="text-primary/45 flex-shrink-0" />
                      : <RefreshCw size={15} className="text-primary/60 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Closing quote */}
            <div className="mt-10 max-w-2xl mx-auto text-center">
              <Quote className="text-primary/40 mx-auto mb-3" size={22} />
              <p className="font-display italic text-foreground/70 text-lg sm:text-xl leading-relaxed" data-testid="chemin-cycle-quote">{c.quote}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
